import { useEffect, useState } from "react";
import { useDragAndDrop } from "../../../hooks/useDragAndDrop";
import { DraggableItem } from "./DraggableItem";

interface DraggableListProps<T> {
  readonly items: T[];
  readonly onReorder: (newItems: T[]) => void;
  readonly renderItem: (item: T, index: number) => React.ReactNode;
  readonly enableDragHandle?: boolean;
  readonly longPressDuration?: number;
  readonly className?: string;
}

export function DraggableList<T extends { id?: string | number }>({
  items,
  onReorder,
  renderItem,
  enableDragHandle = false,
  longPressDuration = 200,
  className = "",
}: DraggableListProps<T>) {
  const [itemHeights, setItemHeights] = useState<number[]>([]);
  const { dragState, handleTouchStart, itemRefs, containerRef } =
    useDragAndDrop<T>({
      items,
      onReorder,
      longPressDuration,
      enableDragHandle,
    });

  // 아이템 refs 초기화 및 높이 측정
  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, items.length);

    // 아이템 높이 측정
    const heights = itemRefs.current.map((el) => el?.offsetHeight || 0);
    setItemHeights(heights);
  }, [items.length]);

  // 드래그 중인 아이템의 새 인덱스 계산
  const getNewIndex = (): number => {
    if (!dragState.isDragging || dragState.draggedIndex === null) {
      return dragState.draggedIndex ?? 0;
    }

    const draggedIndex = dragState.draggedIndex;
    const offsetY = dragState.offsetY;
    const draggedHeight = itemHeights[draggedIndex] || 0;

    if (Math.abs(offsetY) < draggedHeight / 2) {
      return draggedIndex;
    }

    let newIndex = draggedIndex;
    let cumulativeHeight = 0;

    if (offsetY < 0) {
      // 위로 드래그
      for (let i = draggedIndex - 1; i >= 0; i--) {
        cumulativeHeight += itemHeights[i] || 0;
        if (Math.abs(offsetY) > cumulativeHeight - (itemHeights[i] || 0) / 2) {
          newIndex = i;
        } else {
          break;
        }
      }
    } else {
      // 아래로 드래그
      for (let i = draggedIndex + 1; i < items.length; i++) {
        cumulativeHeight += itemHeights[i] || 0;
        if (offsetY > cumulativeHeight - (itemHeights[i] || 0) / 2) {
          newIndex = i + 1;
        } else {
          break;
        }
      }
    }

    return Math.max(0, Math.min(newIndex, items.length - 1));
  };

  const newIndex = dragState.isDragging ? getNewIndex() : null;

  // 각 아이템의 offset 계산
  const getItemOffset = (index: number): number => {
    if (
      !dragState.isDragging ||
      dragState.draggedIndex === null ||
      newIndex === null
    ) {
      return 0;
    }

    const draggedIndex = dragState.draggedIndex;

    if (index === draggedIndex) {
      return 0; // 드래그 중인 아이템은 offsetY로 직접 이동
    }

    const draggedHeight = itemHeights[draggedIndex] || 0;

    // 다른 아이템들의 위치 조정
    if (index < draggedIndex && index >= newIndex) {
      // 위로 이동한 경우: 아래로 밀림
      return draggedHeight;
    } else if (index > draggedIndex && index < newIndex) {
      // 아래로 이동한 경우: 위로 밀림
      return -draggedHeight;
    }

    return 0;
  };

  return (
    <div
      ref={containerRef}
      className={`draggable-list ${className}`}
      style={{
        touchAction: "pan-y",
      }}
    >
      {items.map((item, index) => {
        const itemKey = item.id?.toString() ?? index.toString();
        const offset = getItemOffset(index);
        const isDragging = dragState.draggedIndex === index;

        return (
          <DraggableItem
            key={itemKey}
            ref={(el: HTMLDivElement | null) => {
              itemRefs.current[index] = el;
            }}
            item={item}
            index={index}
            dragState={{
              ...dragState,
              offsetY: isDragging ? dragState.offsetY : offset,
            }}
            onTouchStart={handleTouchStart}
            renderItem={renderItem}
            enableDragHandle={enableDragHandle}
          />
        );
      })}
    </div>
  );
}
